import React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

const getVariant = (message = '') => {
  const m = message.toLowerCase();
  if (m.includes('remove') || m.includes('delete')) return 'remove';
  if (m.includes('cancel')) return 'cancel';
  return 'warn';
};

const VARIANT_CONFIG = {
  remove: {
    icon: <DeleteOutlineIcon sx={{ fontSize: 32, color: '#D32F2F' }} />,
    iconBg: 'rgba(211, 47, 47, 0.09)',
    title: 'Remove Documents',
    btnColor: '#D32F2F',
    btnHover: '#B71C1C',
    btnShadow: 'rgba(211, 47, 47, 0.28)',
  },
  cancel: {
    icon: <CancelOutlinedIcon sx={{ fontSize: 32, color: '#D97706' }} />,
    iconBg: 'rgba(217, 119, 6, 0.09)',
    title: 'Cancel Application',
    btnColor: '#D97706',
    btnHover: '#B45309',
    btnShadow: 'rgba(217, 119, 6, 0.28)',
  },
  warn: {
    icon: <WarningAmberRoundedIcon sx={{ fontSize: 32, color: '#D32F2F' }} />,
    iconBg: 'rgba(211, 47, 47, 0.09)',
    title: 'Confirm Action',
    btnColor: '#D32F2F',
    btnHover: '#B71C1C',
    btnShadow: 'rgba(211, 47, 47, 0.28)',
  },
};

const DeleteDialog = ({
  setViewDeleteDialog,
  viewDeleteDialog,
  deleteAction,
  confirmationMessage,
  leftButtonTitle,
  rightButtonTitle,
}) => {
  const variant = getVariant(confirmationMessage);
  const cfg = VARIANT_CONFIG[variant];

  return (
    <Dialog
      open={viewDeleteDialog}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: '0 24px 64px rgba(8, 57, 61, 0.18)',
        },
      }}
    >
      <DialogContent sx={{ px: 4, pt: 4, pb: 2, textAlign: 'center' }}>
        {/* Icon circle */}
        <Box
          sx={{
            width: 68,
            height: 68,
            borderRadius: '50%',
            background: cfg.iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2.5,
          }}
        >
          {cfg.icon}
        </Box>

        {/* Title */}
        <Typography
          sx={{
            fontFamily: "'Roboto', sans-serif",
            fontSize: '1.125rem',
            fontWeight: 700,
            color: '#08393D',
            mb: 1,
            lineHeight: 1.3,
          }}
        >
          {cfg.title}
        </Typography>

        {/* Message */}
        <Typography
          sx={{
            fontFamily: "'Roboto', sans-serif",
            fontSize: '0.875rem',
            color: '#64748b',
            lineHeight: 1.65,
          }}
        >
          {confirmationMessage ?? 'Are you sure you want to proceed? This action cannot be undone.'}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5 }}>
        {/* Cancel button */}
        <Button
          fullWidth
          variant="outlined"
          onClick={() => setViewDeleteDialog(false)}
          sx={{
            borderRadius: '10px',
            py: 1.25,
            borderColor: 'rgba(12, 74, 80, 0.22)',
            color: '#0B4A50',
            fontWeight: 600,
            fontSize: '0.875rem',
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': {
              borderColor: '#0B4A50',
              background: 'rgba(12, 74, 80, 0.04)',
              boxShadow: 'none',
            },
          }}
        >
          {leftButtonTitle ?? 'Cancel'}
        </Button>

        {/* Confirm button */}
        <Button
          fullWidth
          variant="contained"
          onClick={() => {
            deleteAction();
            setViewDeleteDialog(false);
          }}
          sx={{
            borderRadius: '10px',
            py: 1.25,
            background: cfg.btnColor,
            fontWeight: 600,
            fontSize: '0.875rem',
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': {
              background: cfg.btnHover,
              boxShadow: `0 6px 20px ${cfg.btnShadow}`,
            },
          }}
        >
          {rightButtonTitle ?? 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteDialog;
